import CompanyListFilter from '@/domain/model/company/CompanyListFilter.js';
import CompanyMap from '@/domain/model/company/CompanyMap.js';
import StringUtils from '@/utils/StringUtils.js';
import LabelMap from '@/domain/model/label/LabelMap.js';
import LabelListFilter from '@/domain/model/label/LabelListFilter.js';

import { ref } from 'vue';
import { useConnection } from './useConnection';
import RcParams from '../../domain/common/RcParams';


export function useCompany(domainRef) {

  const { log } = useConnection();

  let loadCompaniesState = ref("");
  let loadCompanyState = ref("");
  let loadedCompanyList = ref([]);
  let loadedCompanyMap = ref(domainRef ? new CompanyMap(domainRef.value) : {});

  const loadDivisions = (company, filterOverrides = {}) => {
    filterOverrides['parent'] = company.idEncoded();
    filterOverrides['company'] = company.idEncoded();
    filterOverrides['includeAddresses'] = true;
    loadCompanies(filterOverrides);
  }

  const loadCompany = () => {
    const domain = domainRef.value;
    if (!domain) {
      log.info("Domain not set");
      return;
    }

    if (loadCompanyState.value === "loading") {
      return;
    }

    loadCompanyState.value = "loading";

    const companyId = domain.session().company().id();
    const operation = domain.events().companys().details(companyId);
    operation.send((response, context) => {
      if (context) {
        //
      }
      loadCompanyState.value = "loaded";
    });
  }

  const loadCompanies = (filterOverrides = {}) => {
    const domain = domainRef.value;
    if (!domain) {
      log.info("Domain not set");
      return;
    }

    loadCompaniesState.value = "loading";

    let filter = createFilter(filterOverrides);

    const operation = domain.events().companys().list(filter);
    if (filterOverrides.includeAddresses) {
      operation.data['includeAddresses'] = filterOverrides.includeAddresses;
    }
    operation.send((response, context) => {
      if (context) {
        //
      }
      const payload = response.payload();
      const payloadMap = payload.valuesFor(CompanyMap.MODEL_NAME);
      loadedCompanyMap.value = new CompanyMap(domain, payloadMap);
      loadedCompanyList.value = loadedCompanyMap.value.sortBy(filterOverrides.sortBy ? filterOverrides.sortBy : 'nameAz');
      loadCompaniesState.value = "loaded";
      log.info("Companies loaded count: {0}", [loadedCompanyList.value.length])
    }, {});
  }

  const createFilter = (query) => {
    const domain = domainRef.value;
    var filter = new CompanyListFilter();
    filter
      .withState(query["status"] ? query["status"] : "active")
      .withPagination(query["pageNumber"] ? query["pageNumber"] : "-1", query["pageSize"] ? query["pageSize"] : "0")
      .done();

    if (!isEmptyOrAll(query.search)) {
      filter
        .withSearchText(query.search)
        .done()
    }

    filter
      .withSortBy(query["sortBy"] ? query["sortBy"] : "nameAz")
      .done();

    if (!isEmptyOrAll(query["paying"])) {
      filter
        .withPaying(query["paying"])
        .done();
    }
    if (!isEmptyOrAll(query["card"])) {
      filter
        .withCardSet(query["card"])
        .done();
    }
    if (!isEmptyOrAll(query["email"])) {
      filter
        .withEmailInvoice(query["email"])
        .done();
    }
    if (!isEmptyOrAll(query["charge"])) {
      filter
        .withChargeInvoice(query["charge"])
        .done();
    }
    if (!isEmptyOrAll(query["feature"])) {
      filter
        .withFeature(query["feature"])
        .done();
    }
    if (!isEmptyOrAll(query["promo"])) {
      filter
        .withPromoCode(query["promo"])
        .done();
    }
    if (!isEmptyOrAll(query["parent"])) {
      filter
        .withParent(domain.companys().findById(query["parent"]))
        .done();
    }
    if (!isEmptyOrAll(query["company"])) {
      filter
        .withCompany(domain.companys().findById(query["company"]))
        .done();
    }
    if (!isEmptyOrAll(StringUtils.toString(query["qbcustomerId"]))) {
      filter
        .withQbCustomerId(query["qbcustomerId"])
        .done();
    }
    return filter;
  };

  let loadLabelsState = ref("loading");
  let loadedLabelList = ref([]);
  let loadedLabelMap = ref(domainRef ? new LabelMap(domainRef.value) : {});

  const loadLabels = (company, query = {}) => {
    query['company'] = company.idEncoded();

    const domain = domainRef.value;
    if (!domain) {
      log.info("Domain not set");
      return;
    }
    loadLabelsState.value = "loading";
    const filter = new LabelListFilter(domain);
    filter
      .withCompany(company)
      .withPagination(query["pageSize"] ? query["pageSize"] : "-1", query["pageNumber"] ? query["pageNumber"] : "0")
      .withStateActive()
      ;
    const params = new RcParams(domain);
    params.withFilter(filter);
    const operation = domain.labels().operations().list(params);
    operation.send((notification, context) => {
      if (notification || context) {
        //
      }
      const payload = notification.payload();
      const payloadMap = payload.valuesFor(LabelMap.MODEL_NAME);
      loadedLabelMap.value = new LabelMap(domain, payloadMap);
      loadedLabelList.value = loadedLabelMap.value.sortBy(query.sortBy ? query.sortBy : 'nameAz');
      loadLabelsState.value = "loaded";
      log.info("Labels loaded count: {0}", [loadedLabelList.value.length])
    }, {});
  }


  const isEmptyOrAll = (value) => {
    return StringUtils.isEmptyOrAll(value)
  };

  return {
    loadLabelsState: loadLabelsState,
    loadedLabelList: loadedLabelList,
    loadedLabelMap: loadedLabelMap,
    loadCompanyLabels: loadLabels,
    loadCompanies: loadCompanies,
    loadCompany: loadCompany,
    loadCompanyState: loadCompanyState,
    loadCompanyDivisions: loadDivisions,
    loadedCompanyList: loadedCompanyList,
    loadedCompanyMap: loadedCompanyMap,
    loadCompaniesState: loadCompaniesState
  }
}
